import { useEffect, useRef, useState } from 'react';

interface Props {
  value: number;
  onChange: (v: number) => void;
  placeholder?: string;
  /** Разрешить отрицательные значения: скидка задаётся минусом. */
  negative?: boolean;
  /** Разрешить дробные. */
  decimal?: boolean;
  disabled?: boolean;
}

function fmt(v: number): string {
  return Number.isFinite(v) ? String(v) : '';
}

/**
 * Числовое поле с промежуточным текстовым состоянием.
 *
 * Наивный вариант `Number(e.target.value) || 0` не даёт стереть содержимое:
 * пустая строка тут же превращается обратно в ноль, и чтобы ввести 15
 * вместо 0, приходится изворачиваться. Отдельные символы «-» и «0.»
 * тоже промежуточные и сами по себе в число не превращаются.
 *
 * Поэтому текст живёт своей жизнью, наружу уходит только разобранное
 * значение, а по уходу из поля содержимое нормализуется.
 */
export default function NumberField({
  value,
  onChange,
  placeholder,
  negative = true,
  decimal = true,
  disabled,
}: Props) {
  const [text, setText] = useState(() => fmt(value));
  // Что мы сами отдали наружу: помогает отличить внешнее изменение
  // (загрузили другую запись) от эха собственного onChange.
  const emitted = useRef(value);

  useEffect(() => {
    if (value !== emitted.current) {
      emitted.current = value;
      setText(fmt(value));
    }
  }, [value]);

  function handle(raw: string) {
    const cleaned = raw.replace(',', '.');

    const allowed = new RegExp(`^${negative ? '-?' : ''}\\d*${decimal ? '(\\.\\d*)?' : ''}$`);
    if (cleaned !== '' && !allowed.test(cleaned)) return;

    setText(cleaned);

    // «-», «.» и «-.» — начало ввода, числа ещё нет. Наружу не отдаём,
    // но и текст не трогаем, иначе минус невозможно набрать.
    if (cleaned === '' || cleaned === '-' || cleaned === '.' || cleaned === '-.') return;

    const n = Number(cleaned);
    if (Number.isFinite(n)) {
      emitted.current = n;
      onChange(n);
    }
  }

  function blur() {
    const n = Number(text);
    if (text.trim() === '' || !Number.isFinite(n)) {
      emitted.current = 0;
      setText('0');
      onChange(0);
      return;
    }
    // Убираем хвосты вида «1.» и «007».
    setText(fmt(n));
  }

  return (
    <input
      // Именно text: у type="number" нельзя прочитать промежуточный ввод —
      // браузер отдаёт пустую строку вместо «-» или «1.».
      type="text"
      inputMode={decimal ? 'decimal' : 'numeric'}
      value={text}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => handle(e.target.value)}
      onBlur={blur}
      onFocus={(e) => e.target.select()}
    />
  );
}
